import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, LogIn, UserCircle, Sparkles, Package, ChefHat } from 'lucide-react'
import { useAuth } from '../contexts/useAuth'

export default function LoginPage() {
    const navigate = useNavigate()
    const { loginWithGoogle, continueAsGuest, isGuest, isAuthenticated, loading } = useAuth()

    useEffect(() => {
        if (!loading && (isAuthenticated || isGuest)) {
            navigate('/app', { replace: true })
        }
    }, [isAuthenticated, isGuest, loading, navigate])

    const handleGuest = () => {
        continueAsGuest()
        navigate('/app')
    }

    const handleGoogle = () => {
        loginWithGoogle()
    }

    return (
        <div style={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 24px',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Decorative orbs */}
            <div style={{
                position: 'absolute', width: 400, height: 400, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12), transparent 70%)',
                top: -80, right: -120, pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute', width: 300, height: 300, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08), transparent 70%)',
                bottom: -50, left: -80, pointerEvents: 'none',
            }} />

            {/* Logo */}
            <div style={{ marginBottom: 12, animation: 'fadeIn 0.5s ease' }}>
                <ShoppingCart size={40} color="var(--primary-light)" />
            </div>
            <h1 style={{
                fontSize: 'clamp(2rem, 6vw, 2.8rem)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                marginBottom: 8,
                animation: 'fadeIn 0.6s ease 0.1s both',
            }}>
                <span className="hero-gradient">EasyMarket</span>
            </h1>
            <p style={{
                color: 'var(--text-2)',
                fontSize: '1rem',
                marginBottom: 40,
                textAlign: 'center',
                maxWidth: 320,
                lineHeight: 1.6,
                animation: 'fadeIn 0.6s ease 0.2s both',
            }}>
                Connectez-vous pour profiter de toutes les fonctionnalités
            </p>

            {/* Login card */}
            <div className="card" style={{
                width: '100%',
                maxWidth: 380,
                padding: '32px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                animation: 'slideUp 0.5s ease 0.3s both',
            }}>
                {/* Google button */}
                <button
                    className="btn btn-full"
                    onClick={handleGoogle}
                    style={{
                        padding: '14px 20px',
                        fontSize: '0.95rem',
                        borderRadius: 14,
                        background: 'var(--surface)',
                        border: '1.5px solid var(--border-2)',
                        color: 'var(--text)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    Continuer avec Google
                </button>

                {/* Features reminder for authenticated users */}
                <div style={{
                    padding: '14px 16px',
                    borderRadius: 12,
                    background: 'var(--primary-glow)',
                    border: '1px solid rgba(38, 67, 39, 0.1)',
                }}>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary-light)', marginBottom: 8 }}>
                        <LogIn size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                        Avec un compte, accédez à :
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {[
                            { icon: Package, text: 'Votre inventaire personnel' },
                            { icon: Sparkles, text: 'La recherche de recettes par IA' },
                            { icon: ChefHat, text: 'Créer vos propres recettes' },
                        ].map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--text-2)' }}>
                                <f.icon size={14} color="var(--primary-light)" />
                                {f.text}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Divider */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    color: 'var(--text-3)',
                    fontSize: '0.8rem',
                }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    ou
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>

                {/* Guest button */}
                <button
                    className="btn btn-ghost btn-full"
                    onClick={handleGuest}
                    style={{
                        padding: '13px 20px',
                        fontSize: '0.9rem',
                        borderRadius: 14,
                    }}
                >
                    <UserCircle size={18} />
                    {isGuest ? 'Continuer en invité' : 'Continuer sans compte'}
                </button>

                <p style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-3)',
                    textAlign: 'center',
                    lineHeight: 1.5,
                }}>
                    En mode invité, vous pouvez générer des listes mais l'inventaire et l'IA ne seront pas disponibles.
                </p>
            </div>

            {/* Back link */}
            <button
                onClick={() => navigate('/')}
                style={{
                    marginTop: 24,
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-3)',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    animation: 'fadeIn 0.6s ease 0.5s both',
                }}
            >
                ← Retour à l'accueil
            </button>
        </div>
    )
}
